import { Button, Flex, ToolbarFilter } from '@patternfly/react-core'
import { DatabaseIcon, PlusIcon, SortAmountDownIcon, SyncAltIcon, TrashAltIcon, UndoIcon } from '@patternfly/react-icons'
import { Toolbar, ToolbarItem, ToolbarContent, ToolbarGroup } from '@patternfly/react-core'
import React from 'react'
import { IModalBasicComponentModalProps, ModalBasicComponent } from '../modal/Modal'
import { ChartsOrderComponent } from './Modals/ChartsOrderComponent'
import { LoadViewComponent } from './Modals/LoadViewComponent'
import { useAxios } from '@app/utils/useAxios'
import { BackofficeAPIConfig } from '@lib/Core/API/BackofficeAPIConfig'
import { BackofficeApiActions } from '@lib/Core/API/BackofficeApiActions'
import { getErrorMessage } from '@lib/Error/getErrorMessage'
import { Filter } from '@app/components/filter/CustomFilters'
import { IChartSet, IFilterSet } from '../page/ComparisonChartsPage'
import { Link } from 'react-router-dom'

interface ComparisonFiltersProps {
  config: Record<string, unknown>
  filterSets: IFilterSet[]
  chartSets: IChartSet[]
  onUpdateViews?: (updatedFilterSets: IFilterSet[]) => void
  onReload?: (index?: number) => void
  onReorderCharts?: (chartData: Record<string, unknown>[]) => void
  onLoadView?: (loadView: Record<string, any>) => void
}

interface UserAnalysisFiltersHandle {
  clear: () => void
  set: (items: Filter[]) => void
}

const DefaultModalComponent: React.FunctionComponent = () => {
  return <></>
}

const component: React.ForwardRefRenderFunction<UserAnalysisFiltersHandle, ComparisonFiltersProps> = (
  props: ComparisonFiltersProps,
  ref: React.ForwardedRef<UserAnalysisFiltersHandle>
) => {
  const api = useAxios()
  const [filterSets, setFilterSets] = React.useState<IFilterSet[]>(props.filterSets || [])

  React.useEffect(() => {
    setFilterSets(props.filterSets || [])
  }, [props.filterSets])

  const onReloadClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()

    if (typeof props.onReload === 'function') {
      props.onReload()
    }
  }

  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const defaultModalProps: IModalBasicComponentModalProps = {
    component: DefaultModalComponent,
    description: '',
    title: '',
    data: {},
  }
  const [modalProps, setModalProps] = React.useState(defaultModalProps)
  const toggleModal = (id: string) => {
    setIsModalOpen(!isModalOpen)

    if (id === chartsOrderModalId && typeof props.onReorderCharts === 'function') {
      const chartData = modalProps.data['chartData'] as Record<string, unknown>[]
      props.onReorderCharts(chartData)
    }

    if (id === loadViewModalId && typeof props.onLoadView === 'function' && modalProps.data['loadView']) {
      props.onLoadView(modalProps.data['loadView'])
    }
  }

  const chartsOrderModalId = 'charts-order-modal'
  const openReorderChartsModal = () => {
    defaultModalProps.title = 'Widgets'
    defaultModalProps.description = `Reorder and choose which widgets to show`
    defaultModalProps.component = ChartsOrderComponent
    defaultModalProps.data = { chartData: props.chartSets[0]?.chartData }
    defaultModalProps.id = chartsOrderModalId
    setIsModalOpen(!isModalOpen)
    setModalProps(defaultModalProps)
  }

  const loadViewModalId = 'load-view-modal'
  const openLoadViewModal = () => {
    defaultModalProps.title = 'Load view'
    defaultModalProps.description = `Load saved view`
    defaultModalProps.component = LoadViewComponent
    defaultModalProps.data = { config: props.config }
    defaultModalProps.id = loadViewModalId
    setIsModalOpen(!isModalOpen)
    setModalProps(defaultModalProps)
  }

  const clearCache = async () => {
    if (confirm(`Clear ${props.config.pageTitle} cache?`)) {
      try {
        await api.current?.post(BackofficeAPIConfig.Domains.Insights, {
          action: BackofficeApiActions.ClearInsightsCache,
          data: { key: props.config.getDataActionName },
        })

        alert('Cleared')
      } catch (error) {
        alert(getErrorMessage(error))
      }
    }
  }

  const onOptionChipDelete = (label: string) => {
    const nextFilterSets = filterSets.filter((n) => n.label !== label)
    if (typeof props.onUpdateViews === 'function') {
      props.onUpdateViews(nextFilterSets)
    }
  }

  const onOptionChipGroupDelete = () => {
    if (typeof props.onUpdateViews === 'function') {
      props.onUpdateViews([])
    }
  }

  const toolbarItems = (
    <React.Fragment>
      <Flex style={{ width: '100%', flexWrap: 'nowrap' }}>
        <Flex grow={{ default: 'grow' }}>
          <ToolbarGroup variant="filter-group" style={{ flexWrap: 'wrap' }}>
            <ToolbarFilter
              key={`tf-select-0`}
              className="pf-u-display-flex pf-u-align-items-center pf-u-mb-md"
              variant="overflow-menu"
              chips={filterSets.map((n) => n.label)}
              deleteChip={(_, chip) => onOptionChipDelete(chip as string)}
              deleteChipGroup={() => onOptionChipGroupDelete()}
              categoryName={'Views'}
              showToolbarItem={props.filterSets.length > 0}
              children={[]}
            ></ToolbarFilter>
          </ToolbarGroup>
        </Flex>
        <Flex>
          <ToolbarGroup variant="filter-group" alignment={{ default: 'alignRight' }}>
            {/* Filter Tools -------------------------------------------------- */}

            <ToolbarItem className="pf-u-mb-md">
              <Button title="Add chart view" variant="plain" onClick={() => openLoadViewModal()} isInline>
                <PlusIcon></PlusIcon>
              </Button>
            </ToolbarItem>

            <ToolbarItem className="pf-u-mb-md">
              <Button title="Reload charts" variant="plain" onClick={(e) => onReloadClick(e)}>
                <SyncAltIcon></SyncAltIcon>
              </Button>
            </ToolbarItem>

            {props.chartSets[0]?.chartData && (
              <ToolbarItem className="pf-u-mb-md">
                <Button title="Reorder charts" variant="plain" onClick={() => openReorderChartsModal()} isInline>
                  <SortAmountDownIcon></SortAmountDownIcon>
                </Button>
              </ToolbarItem>
            )}

            <ToolbarItem className="pf-u-mb-md">
              <Button title="Clear cache" variant="plain" onClick={() => clearCache()} isInline>
                <TrashAltIcon></TrashAltIcon>
              </Button>
            </ToolbarItem>

            <ToolbarItem className="pf-u-mb-md">
              <Button title="Back" variant="plain" component={(props: any) => <Link {...props} to={window.location.pathname.replace('/comparison', '')} />}>
                <UndoIcon></UndoIcon>
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </Flex>
      </Flex>
    </React.Fragment>
  )

  return (
    <React.Fragment>
      <ModalBasicComponent isModalOpen={isModalOpen} handleModalToggle={toggleModal} modalProps={modalProps} toggleModalProps={() => {}} key={null} type={''} props={undefined} />
      <Toolbar
        collapseListedFiltersBreakpoint="md"
        clearFiltersButtonText=""
        inset={{
          default: 'insetNone',
        }}
        style={{ paddingBottom: 0 }}
      >
        <ToolbarContent style={{ marginBottom: '-20px' }}>{toolbarItems}</ToolbarContent>
      </Toolbar>
    </React.Fragment>
  )
}

const ComparisonFilters = React.memo(React.forwardRef(component))

export { ComparisonFilters }
